import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';

import { DeliveryMethodEnum, PositionElementEnum, SecurityVerification, SecurityVerificationEnum, SignatureTypesEnum, Signer, SignerTypeEnum } from '@app/models';
import { AppService, CertificateService, DateService, DocumentService, UserService } from '@app/services';
import { SignatureStepStatus, SignatureStepType } from './to-signature-steps.pipe';
import { CertificateModalService } from '@app/shared/certificate-modal/certificate-modal.service';
import { TranslateService } from '@ngx-translate/core';

interface SignerItemModel {
  class: string;
  status: {
    text: string;
    icon: string;
    badge: string;
  };
  deliveryMethodIcon: string;
  isSignerIcpBrasil: boolean;

  signatureStatus: {
    isSigned: boolean;
    isPending: boolean;
    isPendingIgnoreEmailErrors: boolean;
    isUnnapproved: boolean;
    isBiometricApproved: boolean;
    isSignedAfterBiometricReset: boolean;
  };

  hasVerification: boolean;
  isVerifiedBy: { [key in SecurityVerificationEnum]: boolean };
  hasVerifiableData: boolean;
  isAlreadyVerified: boolean;

  avatarSeed: string;
  company: string;

  isMarkdownDocumentAndWhatsappFlow: boolean;
  isSignatureVisible: boolean;
  isWaitingSortable: boolean;

  shouldShowOptionsToggle: {
    signedBlock: boolean;
    pendingBlock: boolean;
  };
}

@Component({
  selector: 'app-signer-card',
  templateUrl: './signer-card.component.html',
  styleUrls: ['./signer-card.component.scss'],
  standalone: false
})
export class SignerCardComponent implements OnInit, OnChanges {
  readonly SignatureStepStatus = SignatureStepStatus;
  readonly SignatureStepType = SignatureStepType;
  readonly SecurityVerificationEnum = SecurityVerificationEnum;
  readonly DeliveryMethodEnum = DeliveryMethodEnum;
  readonly SignerTypeEnum = SignerTypeEnum;
  readonly certificateTypes = {
    [SignatureTypesEnum.A1]: 'A1',
    [SignatureTypesEnum.A3]: 'A3',
    [SignatureTypesEnum.Safeid]: 'A3',
    [SignatureTypesEnum.Vidaas]: 'A3',
    [SignatureTypesEnum.Birdid]: 'A3'
  };
  @Input() signer: Signer;
  @Input() sortable = false;
  @Input() forceAvatar = false;
  @Input() isAllowedUser = false;
  @Input() isSignerTurn = false;
  @Input() documentAuthorId: string;
  @Input() isMarkdownDocument = false;
  @Input() hideBirthdate = false;
  @Input() hideCPF = false;
  @Input() isDynamicDocument: boolean = false;
  @Input() signerHasFields: boolean = false;
  @Input() isCountryBR: boolean = false;
  @Input() canValidateBiometry = false;
  @Output() updateClick = new EventEmitter<Signer>();
  @Output() deleteClick = new EventEmitter<Signer>();
  @Output() createLinkClick = new EventEmitter<Signer>();
  @Output() consultCPFClick = new EventEmitter<Signer>();
  @Output() resendSignaturesClick = new EventEmitter<Signer>();
  @Output() verifyBiometryClick = new EventEmitter<Signer>();

  signerItem: SignerItemModel = null;

  constructor(
    public router: Router,
    public documentService: DocumentService,
    public userService: UserService,
    public appService: AppService,
    public dateService: DateService,
    private certificateService: CertificateService,
    private certificateModalService: CertificateModalService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.setSignerItemData(this.signer);
  }

  ngOnChanges(changes: SimpleChanges) {
    const relevantInputs = ['signer', 'forceAvatar', 'isAllowedUser', 'isSignerTurn', 'hideBirthdate', 'hideCPF', 'signerHasFields'];
    const hasRealChanges = relevantInputs.some(key => !!changes[key]);

    if (hasRealChanges && changes.signer && changes.signer.currentValue) {
      this.setSignerItemData(changes.signer.currentValue);
    }
  }

  setSignerItemData(signer: Signer) {
    this.signerItem = {
      class: this.getSignerItemClass(signer),
      status: this.getStatusData(signer),
      deliveryMethodIcon: this.getDeliveryMethodIcon(signer),
      isSignerIcpBrasil: this.isSignerIcpBrasil(signer),

      signatureStatus: this.getSignatureStatusData(signer),

      hasVerification: (signer.verifications || []).length > 0,
      isVerifiedBy: this.getVerificationsData(signer),
      hasVerifiableData: this.hasVerifiableData(signer),
      isAlreadyVerified: (signer.verifications || []).some(v => !!v.verified_at),

      avatarSeed: this.getAvatarSeed(signer),
      company: this.getCompanyName(signer),

      isMarkdownDocumentAndWhatsappFlow: this.isMarkdownDocumentAndWhatsappFlow(),
      isSignatureVisible: this.isSignatureVisible(),
      isWaitingSortable: this.isWaitingSortable(),

      shouldShowOptionsToggle: {
        signedBlock: true,
        pendingBlock: false
      }
    };

    this.signerItem.shouldShowOptionsToggle = this.shouldShowOptionsToggle(signer);
  }

  getSignerItemClass(signer: Signer): string {
    if (this.documentService.isSignerSigned(signer)) {
      return 'item--signed';
    }
    if (this.documentService.isSignerRejected(signer) && !this.hasMailError() && !this.hasCelphoneError()) {
      return 'item--refused';
    }

    if (this.documentService.isSignerUnapproved(signer) || this.hasMailError() || this.hasCelphoneError()) {
      return 'item--error';
    }

    if (this.documentService.isSignerPending(signer) && !this.documentService.isSignerUnapproved(signer)) {
      return 'item--pending';
    }
    return '';
  }

  getStatusData(signer: Signer): SignerItemModel['status'] {
    if (this.documentService.isSignerSigned(signer) && (!this.signerHasFields || signer?.fields_filled?.created_at)) {
      return {
        text: !!this.signer.fields_filled?.created_at ? 'filledAndSigned' : 'signed',
        icon: 'signature',
        badge: 'primary'
      };
    } else if (this.documentService.isSignerRejected(signer) && !this.hasMailError() && !this.hasCelphoneError()) {
      return {
        text: 'rejected',
        icon: 'xmark',
        badge: 'error'
      };
    } else if (this.documentService.isSignerBiometricRejected(signer)) {
      return {
        text: 'biometricRejected',
        icon: 'xmark',
        badge: 'error'
      };
    } else if (this.documentService.isSignerBiometricAutoRejected(signer)) {
      return {
        text: 'biometricAutoRejected',
        icon: 'xmark',
        badge: 'error'
      };
    } else if (this.documentService.isSignerUnapproved(signer)) {
      return {
        text: 'awaitingVerification',
        icon: 'clock',
        badge: 'warning'
      };
    } else if (this.hasMailError() || this.hasCelphoneError()) {
      return {
        text: 'sendingFailed',
        icon: 'xmark',
        badge: 'warning'
      };
    } else if (this.documentService.isSignerPending(signer) && !this.documentService.isSignerUnapproved(signer)) {
      return {
        text: 'awaiting',
        icon: 'clock',
        badge: 'neutral'
      };
    }
  }

  getVerificationsData(signer: Signer): SignerItemModel['isVerifiedBy'] {
    const sortedVerifications = (signer.verifications || []).sort(v => (v.verified_at ? 1 : -1));

    return Object.values(SecurityVerificationEnum).reduce(
      (acc, type) => {
        acc[type] = !!sortedVerifications.find(v => v.type === type);
        return acc;
      },
      {} as Record<SecurityVerificationEnum, boolean>
    );
  }

  getSignatureStatusData(signer: Signer): SignerItemModel['signatureStatus'] {
    return {
      isSigned: this.documentService.isSignerSigned(signer),
      isPending: this.documentService.isSignerPending(signer),
      isPendingIgnoreEmailErrors: this.documentService.isSignerPending(signer, { ignoreEmailErrors: true }),
      isUnnapproved: this.documentService.isSignerUnapproved(signer),
      isBiometricApproved: this.documentService.isSignerBiometricApproved(signer),
      isSignedAfterBiometricReset: this.documentService.isSignedAfterBiometricReset(signer)
    };
  }

  getDeliveryMethodIcon(signer: Signer): string {
    if (signer.delivery_method === DeliveryMethodEnum.DeliveryMethodEmail) {
      return 'envelope';
    }
    if (signer.delivery_method === DeliveryMethodEnum.DeliveryMethodSms) {
      return 'message';
    }
    if (signer.delivery_method === DeliveryMethodEnum.DeliveryMethodLink) {
      return 'link';
    }
    if (signer.delivery_method === DeliveryMethodEnum.DeliveryMethodWhatsapp) {
      return 'whatsapp fab';
    }
  }

  isMarkdownDocumentAndWhatsappFlow() {
    return this.isMarkdownDocument && this.isWhatsapp();
  }

  isWhatsapp(signer?: Signer) {
    return (signer || this.signer).delivery_method === DeliveryMethodEnum.DeliveryMethodWhatsapp;
  }

  isSms(signer?: Signer) {
    return (signer || this.signer).delivery_method === DeliveryMethodEnum.DeliveryMethodSms;
  }

  isPhoneDeliveryMethod(signer?: Signer) {
    return this.isWhatsapp(signer) || this.isSms(signer);
  }

  isSignerIcpBrasil(signer?: Signer): boolean {
    if ((signer || this.signer)?.type === SignerTypeEnum.Qualified) return true;
    const payload = (signer || this.signer)?.certificate?.payload;
    return this.certificateService.isIcpBrasilCertificate(payload);
  }

  shouldShowOptionsToggle(signer?: Signer): SignerItemModel['shouldShowOptionsToggle'] {
    if (!this.isAllowedUser)
      return {
        pendingBlock: false,
        signedBlock: false
      };

    signer = signer ?? this.signer;
    const pendingBlock = !!signer.action && !this.signerItem.signatureStatus.isSigned;
    const signedBlock =
      !pendingBlock && this.signerItem.signatureStatus.isSigned && ((signer.user?.birthday && signer.user?.cpf && !this.hideCPF) || this.signerItem.hasVerifiableData || !!signer.certificate);

    return { pendingBlock, signedBlock };
  }

  cpfMask(cpf: string) {
    const cpfSplit = cpf.split(/-|\./);
    return cpf ? `***.${cpfSplit[1]}.${cpfSplit[2]}-**` : cpf;
  }

  birthdayMask(birthday: string) {
    return birthday ? '**/' + birthday.split('/').splice(1).join('/') : birthday;
  }

  isCpf(value: string) {
    value = (value || '').trim().replace(/[^0-9]/g, '');
    return value.length === 11;
  }

  formatCpfCnpj(value: string) {
    value = (value || '').trim().replace(/[^0-9]/g, '');
    if (this.isCpf(value)) {
      return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else {
      return value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
  }

  isSignatureVisible() {
    return (this.signer.positions || []).filter(position => position.element === PositionElementEnum.Signature || position.element === PositionElementEnum.Initials).length > 0;
  }

  isWaitingSortable() {
    return this.sortable && !this.isSignerTurn;
  }

  getAvatarSeed(signer: Signer) {
    return (signer.certificate?.email || '').toLowerCase() || signer.email || signer.user?.name || signer.name || signer.configs?.overwrite_name || signer.user?.phone;
  }

  getCompanyName(signer: Signer) {
    const companyName = signer.user && this.userService.getCompany(signer.user);
    return signer.user && companyName && companyName.toLowerCase().trim() !== 'minha organização' ? companyName : null;
  }

  copySignatureLink() {
    this.documentService.copySignatureLink(this.signer);
  }

  findVerification(signer: Signer, type?: SecurityVerificationEnum): SecurityVerification {
    return (signer.verifications || []).sort(value => (value.verified_at ? 1 : -1)).find(value => value && (!type || value.type === type)); // Prioriza verified_at undefined
  }

  filterVerifications(signer: Signer, type?: SecurityVerificationEnum[]): SecurityVerification[] {
    return (signer.verifications || []).sort(value => (value.verified_at ? 1 : -1)).filter(value => value && (!type || type.includes(value.type))); // Prioriza verified_at undefined
  }

  hasVerifiableData(signer: Signer) {
    return !!this.filterVerifications(signer, this.documentService.allowedManualVerifications)[0]?.user?.images;
  }

  returnEmailErrorMessage(message: string, error: string) {
    // remove first matching word + adds bold to first 3 numbers
    const newStr = message.replace(error + ':', '');
    const finalStr = newStr.replace(/(\d)(\d)(\d)/, match => {
      return `<b>${match}</b>`;
    });
    return finalStr;
  }

  hasMailError() {
    return this.documentService.isSignerError(this.signer) && !this.isPhoneDeliveryMethod();
  }

  hasCelphoneError() {
    return this.documentService.isSignerError(this.signer) && this.isPhoneDeliveryMethod();
  }

  returnErrorReason() {
    const reason = (this.signer.email_events?.reason || 'empty').toLowerCase();

    if (reason.includes('bounce')) return 'bounce';
    if (reason.includes('deferred')) return 'deferred';
    if (reason.includes('blocked')) return 'blocked';
    if (reason.includes('dropped')) return 'dropped';
    if (reason.includes('spamreport')) return 'spamReport';

    return reason;
  }

  getRejectionText(signer: Signer): string {
    if (this.documentService.isSignerBiometricRejected(signer)) {
      return 'signature.biometricRejected';
    } else if (this.documentService.isSignerBiometricAutoRejected(signer)) {
      return 'signature.biometricAutoRejected';
    }
    return 'signature.rejectedDocument';
  }

  openCertificateModal(certificate: string) {
    this.certificateModalService.open({ certificate }).subscribe();
  }

  getWhatsappErrorReason(reason: string): string {
    if (reason?.toLowerCase().includes('payment issue')) {
      return this.translateService.instant('documentHistory.whatsappIncompleteTax');
    }
    return reason;
  }
}
